import axiosInstance from '../utils/axiosInstance';

/**
 * Notification Service
 * Handles REST operations for notifications.
 *
 * Base route: /api/notification
 * Auth: JWT (Bearer) — attached automatically by axiosInstance.
 * Envelope: every response is ApiResponse<T>:
 *   { success, data, message, errors }
 */

/**
 * Unwrap the ApiResponse envelope.
 * Falls back to the raw body when the envelope is not present.
 * @param {Object} response - Axios response
 * @returns {*} The `data` payload
 */
const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return body.data;
  }
  return body;
};

const notificationService = {
  /**
   * Get the list of notifications for the current user.
   * @param {boolean} [unreadOnly=false] - When true, returns only unread notifications.
   * @returns {Promise<Array>} Array of NotificationDto
   */
  async getNotifications(unreadOnly = false) {
    const response = await axiosInstance.get('/api/notification', {
      params: { unreadOnly },
    });
    const data = unwrap(response);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get the number of unread notifications.
   * @returns {Promise<number>} Unread count
   */
  async getUnreadCount() {
    const response = await axiosInstance.get('/api/notification/unread-count');
    const data = unwrap(response);
    return data?.unreadCount ?? 0;
  },

  /**
   * Get a single notification by id.
   * @param {string} id - Notification id (guid)
   * @returns {Promise<Object>} NotificationDto
   */
  async getNotification(id) {
    const response = await axiosInstance.get(`/api/notification/${id}`);
    return unwrap(response);
  },

  /**
   * Mark a single notification as read.
   * @param {string} id - Notification id (guid)
   * @returns {Promise<Object>} Updated NotificationDto
   */
  async markAsRead(id) {
    const response = await axiosInstance.put(`/api/notification/${id}/read`);
    return unwrap(response);
  },

  /**
   * Mark all notifications as read.
   * @returns {Promise<number>} The new unread count (0)
   */
  async markAllAsRead() {
    const response = await axiosInstance.put('/api/notification/read-all');
    const data = unwrap(response);
    return data?.unreadCount ?? 0;
  },

  /**
   * Delete a notification.
   * @param {string} id - Notification id (guid)
   * @returns {Promise<void>}
   */
  async deleteNotification(id) {
    await axiosInstance.delete(`/api/notification/${id}`);
  },

  /**
   * (Admin) Create a notification.
   * @param {Object} payload - CreateNotificationDto
   * @returns {Promise<Object>} Created NotificationDto
   */
  async createNotification(payload) {
    const response = await axiosInstance.post('/api/notification', payload);
    return unwrap(response);
  },
};

export default notificationService;
